import { useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import {
  fmtDate,
  fmtDateTime,
  instructionStatusLabel,
  instructionStatusTone,
  instructionTopicTitle,
  type ProgressInstruction,
} from '@entities/progress';

/* Карточка поручения — общая для реестра Руководителя и «Моих поручений»
   исполнителя. Действия (документ, выполнено, отмена) — снаружи, через
   `actions`. */

const Wrap = styled.article<{ $highlight: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid
    ${({ theme, $highlight }) => ($highlight ? theme.colors.primary : theme.colors.border)};
  box-shadow: ${({ theme }) => theme.shadow.sm};
`;

const Top = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Text = styled.div<{ $clamp: boolean }>`
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-line;
  ${({ $clamp }) =>
    $clamp &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const More = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const Indicator = styled.ul`
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(6)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Due = styled.span<{ $overdue: boolean }>`
  color: ${({ theme, $overdue }) => ($overdue ? theme.colors.status.danger : 'inherit')};
  font-weight: ${({ $overdue }) => ($overdue ? 700 : 400)};
`;

interface Props {
  item: ProgressInstruction;
  /** Показывать исполнителя (реестр) или того, кто выдал (мои поручения). */
  perspective: 'issuer' | 'assignee';
  highlight?: boolean;
  actions?: ReactNode;
}

export function InstructionCard({ item, perspective, highlight = false, actions }: Props) {
  const [open, setOpen] = useState(highlight);
  return (
    <Wrap $highlight={highlight} id={`instruction-${item.id}`}>
      <Top>
        <Title>
          {item.number} · {item.project_name}
        </Title>
        <Badge $tone={instructionStatusTone(item)}>{instructionStatusLabel(item)}</Badge>
      </Top>
      <Meta>
        <span>{instructionTopicTitle(item.topic)}</span>
        {perspective === 'issuer' ? (
          <span>Исполнитель: {item.assignee_name}</span>
        ) : (
          <span>От: {item.issued_by_name ?? 'руководитель'}</span>
        )}
        <span>выдано {fmtDateTime(item.created_at)}</span>
        {item.due_date && <Due $overdue={item.overdue}>срок {fmtDate(item.due_date)}</Due>}
        {item.status === 'done' && item.done_at && (
          <span>выполнено {fmtDateTime(item.done_at)}</span>
        )}
      </Meta>
      <Text $clamp={!open}>{item.text}</Text>
      {open && item.indicator?.lines?.length > 0 && (
        <Indicator aria-label="Показатель на момент выдачи">
          {item.indicator.lines.map(([label, value]) => (
            <li key={label}>
              {label}: {value}
            </li>
          ))}
        </Indicator>
      )}
      {open && item.done_note && <Meta>Отметка исполнителя: {item.done_note}</Meta>}
      <More type="button" onClick={() => setOpen((v) => !v)}>
        {open ? 'Свернуть' : 'Подробнее'}
      </More>
      {actions && <Actions>{actions}</Actions>}
    </Wrap>
  );
}
